/**
 * core/care.ts — the pure rules for FOLLOWER CARE (the owner's "care for the follower
 * like Digimon World 1" wish, with every stress lever removed). Deterministic, no IO —
 * TDD like the rest of `core/`.
 *
 * The anti-DW1 guarantees are load-bearing (see notes/r3-design/follower-care.md):
 *  - Fullness rests at a comfortable FLOOR (never zero), only ever eases DOWN to the
 *    floor with absence, and refills on any interaction.
 *  - Happiness IS the real `PokemonInstance.friendship` (0..255) — every care action
 *    only ever raises it (this is the first friendship-RAISING path in the game).
 *  - Mood is upside-skewed: a peckish partner reads "content", never "sad"/alarmed.
 * Nothing here can punish, decay a stat, or gate anything.
 */
import type { Emotion } from '@/shared';
import { friendshipAfter } from './leveling.js';

export const FULLNESS_MAX = 100;
/** Rest-state after absence; fullness NEVER eases below this. */
export const FULLNESS_FLOOR = 40;
/** Eased toward the floor per elapsed day, clamped at it — never runaway, never zero. */
export const FULLNESS_DRAIN_PER_DAY = 12;

/** Each care action's warm, upside-only effect. friendship uses {@link friendshipAfter}. */
export const CARE_EFFECTS = {
  feed: { fullness: +30, friendship: +2, emotion: 'happy' as Emotion, verb: 'munches happily' },
  praise: {
    fullness: +0,
    friendship: +4,
    emotion: 'joyous' as Emotion,
    verb: 'glows at your praise',
  },
  // Play never lowers fullness (anti-stress): romping is pure upside here.
  play: { fullness: +0, friendship: +5, emotion: 'joyous' as Emotion, verb: 'does a happy skip' },
  rest: { fullness: +18, friendship: +1, emotion: 'sigh' as Emotion, verb: 'settles down cozily' },
} as const;
export type CareAction = keyof typeof CARE_EFFECTS;

/**
 * Map a (friendship, fullness) pair to a display mood → PMD portrait {@link Emotion}.
 * Upside-skewed: a well-loved partner is joyous/happy; a peckish one reads "content"
 * (normal), NEVER sad. There is no unhappy branch by construction.
 */
export function careMood(friendship: number, fullness: number): Emotion {
  if (friendship >= 220) return 'joyous';
  if (friendship >= 140) return 'happy';
  if (fullness <= FULLNESS_FLOOR + 5) return 'normal'; // "content, a touch peckish" — never sad
  return 'happy';
}

/** Ease fullness toward the floor over elapsed days — clamped at the floor, never lower. */
export function fullnessAfterAbsence(fullness: number, elapsedDays: number): number {
  const eased = fullness - FULLNESS_DRAIN_PER_DAY * Math.max(0, elapsedDays);
  return Math.max(FULLNESS_FLOOR, Math.min(FULLNESS_MAX, eased));
}

/** The deltas one care action produces (pure). The caller clamps friendship via leveling. */
export interface CareOutcome {
  readonly friendship: number;
  readonly fullness: number;
  readonly emotion: Emotion;
  readonly verb: string;
}

/**
 * Apply one care action → the values to persist. friendship is clamped to [0,255] via
 * {@link friendshipAfter} (only ever climbs); fullness is clamped to [floor,max]. Pure.
 */
export function applyCare(
  action: CareAction,
  cur: { friendship: number; fullness: number },
): CareOutcome {
  const e = CARE_EFFECTS[action];
  return {
    friendship: friendshipAfter(cur.friendship, e.friendship),
    fullness: Math.max(FULLNESS_FLOOR, Math.min(FULLNESS_MAX, cur.fullness + e.fullness)),
    emotion: e.emotion,
    verb: e.verb,
  };
}
